import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getNestedQueryOptionsParams } from "@app/class/Utilities";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { TranslationService } from "@app/services/translation.service";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { IReportHistory } from "../models/IDynamicReportLog";
import { IReportParameter, IReportParameters, ReportParameterType } from "../models/IReportParameters";
import { ReportHistoryQueryOptions } from "../models/ReportHistoryQueryOptions";
import { DynamicReportService } from "./dynamicReports.service";

@Injectable({
    providedIn: 'root'
})
export class ReportHistoryService {
    private reportHistoriesCacheName: string = "reportHistoriesCache";
    constructor(
        private readonly http: HttpClient,
        private readonly dataCacheService: DataCacheService,
        private readonly dynamicReportService: DynamicReportService,
        private readonly translationService: TranslationService
    ) {
    }

    getLatestReportHistory(invalidateCache = false): Observable<IReportHistory[]> {
        return this.dataCacheService.getOrRequest(this.reportHistoriesCacheName, () => this.getLatestReportHistoryFromDb(), { invalidateCache }).pipe(map((logs: IReportHistory[]) => {

            const successfulLogs: IReportHistory[] = this.setupLogs(logs);

                return successfulLogs;
            }));

    }

    getReportHistory(options: ReportHistoryQueryOptions = {}): Observable<IReportHistory[]> {
        const params = getNestedQueryOptionsParams(options);

        return this.http.get<IReportHistory[]>(`/api/reports/logs`, params)
            .pipe(map((reportLogs: IReportHistory[]) => {
                return this.setupLogs(reportLogs);
            }));
    }
    
    createParameterString(reportLog: IReportHistory): string {
        let paramsString = "";
        let paramsArr = [];

        const params = reportLog.Parameters;
        const customParams = params.CustomParameters;

        const customParamsString = this.getCustomParamsAsString(customParams);
        customParamsString && paramsArr.push(customParamsString);

        const groupParamsString = this.getGroupParamsAsString(params);
        groupParamsString && paramsArr.push(groupParamsString)

        paramsString = paramsArr.join(", ");
        return paramsString;
    }

    getSingleReportHistory(historyId: number): Observable<IReportHistory> {
        return this.getReportHistory({ Id: historyId }).pipe(
            map(history => {
                return history[0]
            })
        )
    }

    private getGroupParamsAsString(params: IReportParameters): string {
        if (!params) {
            return;
        }

        let paramsArr = [];

        if (params.DriverGroupId) {
            paramsArr.push(this.translationService.t("ReportHistory.DriversInGroup", params.DriverGroupId));
        }

        if (params.VehicleGroupId) {
            paramsArr.push(this.translationService.t("ReportHistory.VehiclesInGroup", params.VehicleGroupId));
        }

        if (params.GeofenceGroup) {
            paramsArr.push(this.translationService.t("ReportHistory.GeofencesInGroup", params.GeofenceGroupId));
        }

        if (!paramsArr.length) {
            return;
        }

        return paramsArr.join(", ");
    }

    private getCustomParamsAsString(params: IReportParameter[]): string {
        let paramsArr = [];

        if (!params?.length) {
            return;
        }

        params.forEach(param => {
            if (param.Value !== null && param.Active) {
                paramsArr.push(this.translationService.t(`Reports.${ReportParameterType[param.ParameterType]}`));
            }
        })

        if (!paramsArr.length) {
            return;
        }

        return paramsArr.join(", ");
    }

    private getLatestReportHistoryFromDb(): Observable<IReportHistory[]> {
        return this
            .http.get<IReportHistory[]>("/api/reports/logs/latest");            
    }

    private setupLogs(logs: IReportHistory[]): IReportHistory[] {
        const successfulLogs: IReportHistory[] = [];
        logs.forEach(reportLog => {
            if (!reportLog.Success) {
                return;
            }

            this.dynamicReportService.formatReportLog(reportLog);

            successfulLogs.push(reportLog);
        });

        successfulLogs.sort((x, y) => {
            return y.Id - x.Id;
        });

        return successfulLogs;
    }
    
}
